/**
 * LiveTestingSxSelectField
 *
 * sx_select 타입 질문을 위한 필드 컴포넌트
 * V1 QsSxCollector의 V2 버전
 *
 * 기능:
 * - 증상 옵션 체크박스 목록 표시
 * - 옵션 클릭 시 해당 트리의 질문들을 모달에서 표시
 * - mutuallyExclusive 옵션 처리
 */

import { memo, useMemo } from 'react';
import classNames from 'classnames';
import type { QuestionResponseDto } from '../../../../api/generated/types';
import type { V2QsSelectValue } from '../../../../shared/components/question-types/V2QsSelect';
import { useStaticLocales, type SystemLocaleCodes } from '../../../../../admin/hooks/locales/use-static-locale';
import { useLiveTestingSxSelect } from './useLiveTestingSxSelect';
import LiveTestingSxSelectModal from './LiveTestingSxSelectModal';
import LiveTestingContainer from './LiveTestingContainer';
import { getLocaleCode } from '../../../organization/configurations/language/constants';

interface LiveTestingSxSelectFieldProps {
  /** 질문 데이터 */
  question: QuestionResponseDto;
  /** 현재 답변 값 */
  value: V2QsSelectValue;
  /** 값 변경 핸들러 */
  onChange: (value: V2QsSelectValue) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
}

function LiveTestingSxSelectField({
  question,
  value,
  onChange,
  disabled = false,
}: LiveTestingSxSelectFieldProps) {
  const { answerKeys, answerOptionsMap, typeOptions } = question;

  // suppressSxCollectors 옵션 (증상 트리 내 sx_select 질문 숨김)
  // TypeOptionsDto에 정의되지 않은 확장 필드이므로 타입 단언 사용
  const suppressSxCollectors = !!(typeOptions as Record<string, unknown> | undefined)?.suppressSxCollectors;

  // Container에서 surveyId, locale 가져오기
  const { surveyId, currentLocale } = LiveTestingContainer.useContainer();

  const localeCode = getLocaleCode(currentLocale || 'english') as SystemLocaleCodes;
  const { sltStr } = useStaticLocales({ localeCode });

  // sx_select hook 사용
  const {
    options,
    modalState,
    handleOptionClick,
    handleModalClose,
    handleModalSave,
    updateModalAnswer,
    isLoadingOptions,
  } = useLiveTestingSxSelect({
    answerKeys: answerKeys || [],
    answerOptionsMap: answerOptionsMap || null,
    value: value || { selectedKeys: [], notesForKeys: {} },
    onChange,
    locale: currentLocale,
    surveyId: surveyId ? Number(surveyId) : undefined,
    suppressSxCollectors,
  });

  // 현재 선택된 증상 이름
  const selectedSymptomName = useMemo(() => {
    if (!modalState.selectedSxKey) return '';
    const option = options.find((opt) => opt.key === modalState.selectedSxKey);
    return option?.label || modalState.selectedSxKey;
  }, [modalState.selectedSxKey, options]);

  const getOptionClasses = (isSelected: boolean, isDisabled: boolean) =>
    classNames(
      'flex flex-col gap-3 transition-all duration-200',
      'border-[1.5px] p-3 rounded-[10px]',
      {
        'border-[#4B7BF5]': isSelected && !isDisabled,
        'border-[#E6E9F5]': !isSelected && !isDisabled,
        'border-[#D0D4E4]': isDisabled,
        'bg-white': !isDisabled,
        'bg-[#F8F9FC]': isDisabled,
        'hover:border-[#DADDEB]': !isSelected && !isDisabled,
        'cursor-default': isDisabled,
        'cursor-pointer': !isDisabled,
      }
    );

  const inputClasses = (isDisabled: boolean) =>
    classNames('checkbox checkbox-lg checkbox-primary shrink-0', {
      '!border-[2.5px] !border-[#9CA3B8]': isDisabled,
    });

  if (isLoadingOptions) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: Math.min(options.length || 3, 6) }).map((_, i) => (
          <div key={i} className="animate-pulse border-[1.5px] border-[#E6E9F5] p-3 rounded-[10px] h-12 bg-gray-50" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* 옵션 목록 */}
      {options.map((option) => {
        const isDisabled = Boolean(disabled || option.disabled);
        return (
          <label
            key={option.key}
            className={getOptionClasses(option.checked, isDisabled)}
          >
            <div className="flex-1 w-full flex flex-row gap-4 items-start">
              <input
                type="checkbox"
                checked={option.checked}
                disabled={!!isDisabled}
                className={inputClasses(isDisabled)}
                onChange={() => {
                  if (isDisabled) return;
                  handleOptionClick(option.key);
                }}
              />
              <span
                className="pt-[0.2rem] text-[15px] text-[#222638]"
                onClick={() => {
                  if (isDisabled) return;
                  handleOptionClick(option.key);
                }}
              >
                {option.label}
              </span>
            </div>
          </label>
        );
      })}

      {/* 증상 상세 모달 */}
      <LiveTestingSxSelectModal
        isOpen={modalState.isOpen}
        symptomName={selectedSymptomName}
        questions={modalState.questions}
        answers={modalState.answers}
        onAnswerChange={updateModalAnswer}
        onSave={handleModalSave}
        onClose={handleModalClose}
        isLoading={modalState.isLoading}
        error={modalState.error}
        saveButtonText={sltStr({ key: 'btn-save', capit: true })}
      />
    </div>
  );
}

export default memo(LiveTestingSxSelectField);
